import pino from 'pino'
import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'
import type { LoggingConfig } from '../config/config.js'
import { sanitizeNetworkLogValue } from '../security/network-redaction.js'

export type Logger = pino.Logger

const networkSecurityHooks = {
  logMethod(inputArgs: unknown[], method: (...args: unknown[]) => void) {
    method.apply(this, inputArgs.map((arg) => sanitizeNetworkLogValue(arg)))
  },
}

// --- Default console-only logger (pre-init) ---
// Before initLogger() is called (e.g., during module-level imports),
// a basic pino-pretty logger writes to stderr. initLogger() replaces it
// with a multi-target logger (console + rolling file).
let rootLogger: pino.Logger = pino({
  level: 'debug',
  hooks: networkSecurityHooks,
  transport: { target: 'pino-pretty', options: { colorize: true, translateTime: 'SYS:standard', destination: 2 } },
})
let initialized = false
let logDir: string | undefined
let currentTransport: ReturnType<typeof pino.transport> | undefined

function expandHome(p: string): string {
  return p.startsWith('~') ? path.join(os.homedir(), p.slice(1)) : p
}

// --- Variadic wrapper for backward compatibility ---
// Pino's API requires (obj, msg) or (msg) — this wrapper allows
// callers to use log.info(obj, ...strings) or log.info(...strings),
// matching the more permissive console.log-style API.
function wrapVariadic(logger: pino.Logger) {
  return {
    info: (...args: unknown[]) => {
      if (args.length === 0) return
      if (typeof args[0] === 'object' && args[0] !== null && !(args[0] instanceof Error)) {
        logger.info(args[0] as object, args.slice(1).join(' '))
      } else {
        logger.info(args.map(String).join(' '))
      }
    },
    warn: (...args: unknown[]) => {
      if (args.length === 0) return
      if (typeof args[0] === 'object' && args[0] !== null && !(args[0] instanceof Error)) {
        logger.warn(args[0] as object, args.slice(1).join(' '))
      } else {
        logger.warn(args.map(String).join(' '))
      }
    },
    error: (...args: unknown[]) => {
      if (args.length === 0) return
      if (typeof args[0] === 'object' && args[0] !== null && !(args[0] instanceof Error)) {
        logger.error(args[0] as object, args.slice(1).join(' '))
      } else {
        logger.error(args.map(String).join(' '))
      }
    },
    debug: (...args: unknown[]) => {
      if (args.length === 0) return
      if (typeof args[0] === 'object' && args[0] !== null && !(args[0] instanceof Error)) {
        logger.debug(args[0] as object, args.slice(1).join(' '))
      } else {
        logger.debug(args.map(String).join(' '))
      }
    },
    fatal: (...args: unknown[]) => {
      if (args.length === 0) return
      if (typeof args[0] === 'object' && args[0] !== null && !(args[0] instanceof Error)) {
        logger.fatal(args[0] as object, args.slice(1).join(' '))
      } else {
        logger.fatal(args.map(String).join(' '))
      }
    },
    child: (bindings: pino.Bindings) => logger.child(bindings),
  }
}

export const log = wrapVariadic(rootLogger)

// --- Mute/unmute (suppress pino output during interactive prompts) ---

let muteCount = 0
let savedLevel = 'info'

/**
 * Suppress all log output. Calls are ref-counted — each muteLogger()
 * must be balanced by an unmuteLogger(). Used during interactive CLI
 * prompts (e.g., setup wizard) to avoid log noise in the terminal.
 *
 * Must always be paired with a corresponding `unmuteLogger()` call;
 * otherwise the logger remains permanently silenced.
 */
export function muteLogger(): void {
  if (muteCount === 0) {
    savedLevel = rootLogger.level
    rootLogger.level = 'silent'
  }
  muteCount++
}

/** Decrement the mute ref count and restore log level when it reaches zero. */
export function unmuteLogger(): void {
  muteCount--
  if (muteCount <= 0) {
    muteCount = 0
    rootLogger.level = savedLevel
  }
}

// --- Public API ---

/**
 * Initialize the root logger with file + console output.
 *
 * Sets up a multi-target pino transport: pino-pretty to stderr (for humans)
 * and pino-roll to a rotating log file (for persistence). Also creates
 * the sessions/ subdirectory for per-session log files.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initLogger(config: LoggingConfig): Logger {
  if (initialized) return rootLogger

  const resolvedLogDir = expandHome(config.logDir)
  logDir = resolvedLogDir

  try {
    fs.mkdirSync(resolvedLogDir, { recursive: true })
    fs.mkdirSync(path.join(resolvedLogDir, 'sessions'), { recursive: true })
  } catch (err) {
    console.error(`[WARN] Failed to create log directory ${resolvedLogDir}, falling back to console-only:`, err)
    return rootLogger
  }

  const transports = pino.transport({
    targets: [
      {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'SYS:HH:MM:ss',
          ignore: 'pid,hostname',
          singleLine: true,
          destination: 2,
        },
        level: config.level,
      },
      {
        target: 'pino-roll',
        options: {
          file: path.join(resolvedLogDir, 'openacp.log'),
          size: config.maxFileSize,
          limit: { count: config.maxFiles },
        },
        level: config.level,
      },
    ],
  })

  currentTransport = transports
  rootLogger = pino({ level: config.level, hooks: networkSecurityHooks }, transports)
  initialized = true

  // Update the default log wrapper to use the new root logger
  Object.assign(log, wrapVariadic(rootLogger))

  return rootLogger
}

/** Change log level at runtime. Pino transport targets respect parent level changes automatically. */
export function setLogLevel(level: string): void {
  rootLogger.level = level
}

/**
 * Create a child logger scoped to a module (e.g., "core", "session", "telegram").
 *
 * Returns a Proxy that always delegates to the current rootLogger — this
 * ensures child loggers created at module-level (before initLogger runs)
 * pick up the initialized logger with proper transports once it's ready.
 */
export function createChildLogger(context: { module: string; [key: string]: unknown }): Logger {
  return new Proxy({} as Logger, {
    get(_target, prop, receiver) {
      const child = rootLogger.child(sanitizeNetworkLogValue(context) as pino.Bindings)
      const value = Reflect.get(child, prop, receiver)
      return typeof value === 'function' ? value.bind(child) : value
    },
  })
}

/**
 * Create a per-session logger that writes to both the combined log and
 * a session-specific file (`<logDir>/sessions/<sessionId>.log`).
 *
 * This dual-write pattern allows operators to view all logs in one place
 * (combined log) while also being able to inspect a single session's
 * activity in isolation (session file).
 *
 * Falls back to a simple child logger if the session log file can't be created.
 */
export function createSessionLogger(sessionId: string, parentLogger: Logger): Logger {
  const sessionLogDir = logDir ? path.join(logDir, 'sessions') : undefined
  if (!sessionLogDir) {
    return parentLogger.child({ sessionId })
  }

  try {
    const sessionLogPath = path.join(sessionLogDir, `${sessionId}.log`)
    const dest = pino.destination(sessionLogPath)
    const sessionFileLogger = pino({ level: parentLogger.level, hooks: networkSecurityHooks }, dest).child({ sessionId })

    // Create a logger that writes to both parent (combined) and session file
    const combinedChild = parentLogger.child({ sessionId })
    const originalInfo = combinedChild.info.bind(combinedChild)
    const originalWarn = combinedChild.warn.bind(combinedChild)
    const originalError = combinedChild.error.bind(combinedChild)
    const originalDebug = combinedChild.debug.bind(combinedChild)
    const originalFatal = combinedChild.fatal.bind(combinedChild)

    // Proxy log methods to write to both destinations
    combinedChild.info = ((objOrMsg: any, ...rest: any[]) => {
      sessionFileLogger.info(objOrMsg, ...rest)
      return originalInfo(objOrMsg, ...rest)
    }) as any
    combinedChild.warn = ((objOrMsg: any, ...rest: any[]) => {
      sessionFileLogger.warn(objOrMsg, ...rest)
      return originalWarn(objOrMsg, ...rest)
    }) as any
    combinedChild.error = ((objOrMsg: any, ...rest: any[]) => {
      sessionFileLogger.error(objOrMsg, ...rest)
      return originalError(objOrMsg, ...rest)
    }) as any
    combinedChild.debug = ((objOrMsg: any, ...rest: any[]) => {
      sessionFileLogger.debug(objOrMsg, ...rest)
      return originalDebug(objOrMsg, ...rest)
    }) as any
    combinedChild.fatal = ((objOrMsg: any, ...rest: any[]) => {
      sessionFileLogger.fatal(objOrMsg, ...rest)
      return originalFatal(objOrMsg, ...rest)
    }) as any

    // Store dest for cleanup
    ;(combinedChild as any).__sessionDest = dest

    return combinedChild
  } catch (err) {
    // Graceful degradation: session file failed, just use combined log
    parentLogger.warn({ sessionId, err }, 'Failed to create session log file, using combined log only')
    return parentLogger.child({ sessionId })
  }
}

/** Close the per-session log file destination to release the file handle. */
export async function closeSessionLogger(logger: Logger): Promise<void> {
  const dest = (logger as any).__sessionDest
  if (!dest) return
  await new Promise<void>((resolve) => {
    let settled = false
    const done = () => { if (!settled) { settled = true; resolve() } }
    const timeout = setTimeout(done, 3000)
    dest.once?.('close', () => { clearTimeout(timeout); done() })
    if (typeof dest.end === 'function') dest.end()
    else { dest.destroy?.(); clearTimeout(timeout); done() }
  })
}

/**
 * Flush and close the root logger transport.
 *
 * Resets all state so the logger can be re-initialized (e.g., after restart).
 * Waits up to 3 seconds for the transport to close gracefully.
 */
export async function shutdownLogger(): Promise<void> {
  if (!initialized) return

  const transport = currentTransport

  // Reset state immediately so re-init is possible
  rootLogger = pino({ level: 'debug', hooks: networkSecurityHooks })
  Object.assign(log, wrapVariadic(rootLogger))
  currentTransport = undefined
  logDir = undefined
  initialized = false

  if (transport) {
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, 3000)
      transport.on('close', () => {
        clearTimeout(timeout)
        resolve()
      })
      transport.end()
    })
  }
}

/**
 * Delete session log files older than the given retention period.
 *
 * Called during startup to prevent unbounded disk usage from accumulated
 * per-session log files.
 */
export async function cleanupOldSessionLogs(retentionDays: number): Promise<void> {
  if (!logDir) return

  const sessionsDir = path.join(logDir, 'sessions')
  try {
    const files = await fs.promises.readdir(sessionsDir)
    const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000

    for (const file of files) {
      try {
        const filePath = path.join(sessionsDir, file)
        const stat = await fs.promises.stat(filePath)
        if (stat.mtimeMs < cutoff) {
          await fs.promises.unlink(filePath)
          rootLogger.debug({ file }, 'Deleted old session log')
        }
      } catch (err) {
        rootLogger.warn({ file, err }, 'Failed to delete old session log')
      }
    }
  } catch {
    // Sessions directory doesn't exist — no-op
  }
}
